<script lang="ts">
  import { Component, Vue, Prop } from 'vue-property-decorator';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import SimpleScrollableModal from '../base/SimpleScrollableModal.vue';
  import ItemsService from '@/api/services/items.service';
  import { IItem } from '@/interfaces/IItem';
  import CategoriesService from '@/api/services/categories.service';
  import RestaurantsService from '@/api/services/restaurants.service';
  import { ICategory } from '@/interfaces/ICategory';
  import { LabelPrinter } from '@/lib/print/LabelPrinter';
  import { vueData } from '@/VueData';
  import { AlertType } from '@/interfaces/IAlert';
  import { IOrderItem } from '@/interfaces/IOrderItem';
  import AddItemWidget from '@/components/widgets/AddItemWidget.vue';
  import {
    ItemLabelVariables,
    ItemLabelVariablesMap,
  } from '@/const/ItemLabelVariables';
import { usePrinting } from '@/composition/use-printing';
import { useMenuStore } from '@/composition/use-menu';

  @Component({
    components: { SimpleScrollableModal, AddItemWidget },
  })
  export default class StickerPrintModal extends Vue {
    @Prop() item: IItem;
    optionsValid = false;
    orderItem: IOrderItem = null;
    formValid = true;
    printing = usePrinting();
    menuStore = useMenuStore();

    async created() {}

    mounted() {
      if (!this.item.labelTemplate) {
        vueData.addAlert(
          'Este producto no tiene una etiqueta asignada. Por favor, asigna una etiqueta antes de imprimir.',
          AlertType.Error
        );
        this.$emit('close');
        return;
      }

      this.loadManualVariables();
    }

    async print() {
      try {
        await LabelPrinter.printItemLabel({
          item: this.item,
          orderItem: this.orderItem,
          manualVariables: this.manualVariables,
          itemMap: this.menuStore.itemMap,
        });
        this.$parent.$emit('printed');
        this.$emit('close');
      } catch (e) {
        vueData.addAlert(
          'No se pudo imprimir la etiqueta. Por favor, verifique que la impresora esté conectada y encendida.',
          AlertType.Error
        );
      } finally {
        this.storeManualVariables();
      }
    }

    // Store manual variables value on sessionstorage
    async storeManualVariables() {
      try {
        const manualVariables = this.manualVariables;
        const manualVariablesObject = this.manualVariables.reduce(
          (acc, v) => ({ ...acc, [v.key]: v.value }),
          {}
        );
        sessionStorage.setItem(
          `item-label-manual-variables`,
          JSON.stringify(manualVariablesObject)
        );
      } catch (e) {
        console.error(e);
      }
    }

    async loadManualVariables() {
      try {
        const manualVariables = this.manualVariables;
        const manualVariablesMap = new Map(
          manualVariables.map((v) => [v.key, v])
        );
        const storedManualVariables = JSON.parse(
          sessionStorage.getItem(
            `item-label-manual-variables`
          ) || '{}'
        );
        for (const [key, value] of Object.entries(storedManualVariables)) {
          if (manualVariablesMap.has(key)) {
            manualVariablesMap.get(key).value = (value as string) || '';
          }
        }
      } catch (e) {
        console.error(e);
      }
    }

    onOrderItemChanged(orderItem: IOrderItem) {
      this.orderItem = orderItem;
    }

    get restaurant() {
      return vueData.restaurant;
    }

    get branch() {
      return vueData.branch;
    }

    get currency() {
      return vueData.currency;
    }

    get restaurantSettings() {
      return vueData.restaurantSettings;
    }

    get manualVariables() {
      const autoVariables = ItemLabelVariables.filter((v) => !v.onlyOrder);
      const autoVariablesMap = new Map(autoVariables.map((v) => [v.key, v]));
      // Get all alphanumeric strings prefixed by $ from item.labelTemplate (that don't start with numbers)
      const currentVariables = (
        this.item.labelTemplate.match(/(?<!\d)\$\w+/g) || []
      ).map((v) => v.substr(1));
      const manualVariables = currentVariables.filter(
        (v) => !autoVariablesMap.has(v)
      );

      return manualVariables.map((v) => {
        if (ItemLabelVariablesMap.has(v)) {
          return { ...ItemLabelVariablesMap.get(v), value: '' };
        } else {
          return {
            key: v,
            title: v
              .split('_')
              .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
              .join(' '),
            value: '',
          };
        }
      });
    }
  }
</script>

<template>
  <simple-scrollable-modal>
    <template #title>
      <h3>Etiqueta de {{ item.name }}</h3>
    </template>
    <template #content>
      <template v-if="manualVariables.length">
        <v-form @submit.prevent v-model="formValid" class="variables-form">
          <div
            class="variable"
            v-for="variable of manualVariables"
            :key="variable.key"
          >
            <h4 class="variable-title">{{ variable.title }}</h4>
            <!-- select all textfield on click -->
            <v-text-field
              :placeholder="variable.title"
              v-model="variable.value"
              outlined
              dense
              :rules="$rule('required')"
              @click="$event.target.select()"
            ></v-text-field>
          </div>
        </v-form @submit.prevent>
      </template>
      <AddItemWidget
        :hide-header="true"
        :hide-prices="true"
        :restaurant="restaurant"
        :currency="currency"
        :restaurantSettings="restaurantSettings"
        :branch="branch"
        :item="item"
        :final-options="item.options"
        @change="onOrderItemChanged"
        @on-validation="optionsValid = $event"
      ></AddItemWidget>
    </template>
    <template #action-bar>
      <div class="d-flex flex-row justify-end">
        <v-btn color="danger" text depressed @click="$emit('close')">
          Cerrar
        </v-btn>
        <v-btn
          color="info"
          depressed
          @click="print"
          :disabled="!optionsValid || !formValid"
        >
          Imprimir
        </v-btn>
      </div>
    </template>
  </simple-scrollable-modal>
</template>

<style scoped lang="scss">
  .variables-form {
    .variable {
      .variable-title {
        @apply tw-font-bold;
      }
    }
  }
</style>